'use client';
// ^ Necessary if using App Router to ensure this file runs on the client

import { datadogLogs, LogsEvent, LogsEventDomainContext } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { organizationFromEndPoint } from '@utils/index';

const endpoint = process.env.CL_ENDPOINT;

const DRAFT_ENV = process.env.DRAFT_ENV || 'prod';
const DD_APPLICATION_ID = process.env.DD_APPLICATION_ID || false;
const DD_CLIENT_TOKEN = process.env.DD_CLIENT_TOKEN || false;

if (DD_CLIENT_TOKEN) {
  // Generic regex to intercept bots/crawlers/spiders plus any known UAs
  const botRegex = new RegExp(
    [
      // Generic bot/crawler/spider/scanner/fetch/etc indicators
      '\\b(bot|crawler|spider|crawl|slurp|fetch|scan|analyz|index|scrap|monitor|preview)\\b',
      // Some known bots that may not have standard names
      'google favicon',
      'google web preview',
      'facebookexternalhit',
      'facebot',
      'pingdom',
      'uptime',
      'python-urllib',
      'libwww-perl',
      'java',
      'curl',
      'wget',
    ].join('|'),
    'i' // Case insensitive
  );

  const isBot = typeof window !== 'undefined' ? botRegex.test(navigator.userAgent) : false;

  const organization = organizationFromEndPoint(endpoint);

  datadogLogs.init({
    clientToken: DD_CLIENT_TOKEN,
    site: 'datadoghq.eu',
    service: 'wineplatform-' + organization,
    env: DRAFT_ENV,
    forwardErrorsToLogs: true,
    sessionSampleRate: isBot ? 0 : 100,
    version: '0.0.1',
    beforeSend: (event: LogsEvent, context: LogsEventDomainContext) => {
      // skip log script error -> https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#script-error
      if (['Uncaught "Script error."', 'Error: Script error.'].includes(event.message)) {
        return false;
      }
      // skip analytics network error
      if (event.origin == 'network' && event.http?.url.includes('analytics')) {
        return false;
      }
      return true;
    },
  });
  if (DD_APPLICATION_ID) {
    datadogRum.init({
      applicationId: DD_APPLICATION_ID,
      clientToken: DD_CLIENT_TOKEN,
      site: 'datadoghq.eu',
      service: 'wineplatform-' + organization,
      env: DRAFT_ENV,
      version: '0.0.1',
      sessionSampleRate: isBot ? 0 : 10,
      sessionReplaySampleRate: isBot ? 0 : 1,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      // Specify URLs to propagate trace headers for connection between RUM and backend trace
      allowedTracingUrls: [
        //{ match: 'https://example.com/api/', propagatorTypes: ['tracecontext'] },
        // do not propagate trace headers to the CL endpoint, CORS will block the request and throw an error in the console
        //{ match: process.env.CL_ENDPOINT, propagatorTypes: ['tracecontext'] },
        { match: process.env.HOST, propagatorTypes: ['tracecontext'] },
      ],
    });
  }
}

export default function DatadogInit(): null {
  // Render nothing - this component is only included so that the init code
  // above will run client-side
  return null;
}
